import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Seo from '../seo/Seo';

const EFFECTIVE_DATE = 'May 10, 2025';

const sections = [
  {
    id: 'introduction',
    title: '1. Introduction',
    content: (
      <>
        <p>
          Welcome to MyRoomsAI (<a href="https://myroomsai.com">https://myroomsai.com</a>). By
          accessing or using our website and services, you agree to be bound by these Terms and
          Conditions. Please read them carefully before using the service.
        </p>
        <p>
          MyRoomsAI provides an AI-powered room redesign and interior design image generation
          service. Users upload photos of their rooms and receive AI-generated redesign images based
          on their selected preferences.
        </p>
        <p>
          If you do not agree to these Terms, please do not use the service.
        </p>
      </>
    ),
  },
  {
    id: 'use-of-service',
    title: '2. Use of the Service',
    content: (
      <>
        <p>
          MyRoomsAI allows registered users to upload room photos and generate AI-redesigned images
          by selecting options such as room type, design style, decoration level, color preferences,
          and optional notes.
        </p>
        <p>
          The service is intended for personal and professional use including interior design
          inspiration, visualization, and design planning. You agree to use the service only for
          lawful purposes and in accordance with these Terms.
        </p>
        <p>
          You must be at least 18 years old, or the age of majority in your jurisdiction, to use
          this service.
        </p>
      </>
    ),
  },
  {
    id: 'user-accounts',
    title: '3. User Accounts',
    content: (
      <>
        <p>
          To access the generation features of MyRoomsAI, you must create an account. You are
          responsible for maintaining the confidentiality of your account credentials and for all
          activity that occurs under your account.
        </p>
        <p>
          You agree to provide accurate and up-to-date information when registering, and to notify
          us promptly of any unauthorized use of your account.
        </p>
        <p>
          We reserve the right to suspend or terminate accounts that violate these Terms or that we
          believe pose a risk to the service or other users.
        </p>
      </>
    ),
  },
  {
    id: 'image-uploads',
    title: '4. Image Uploads and User Content',
    content: (
      <>
        <p>
          You retain ownership of any images you upload to MyRoomsAI. By uploading an image, you
          grant MyRoomsAI a limited, non-exclusive licence to process that image solely for the
          purpose of generating your requested AI redesign.
        </p>
        <p>
          You are solely responsible for ensuring that you have the right to upload any image you
          provide. You must not upload images that:
        </p>
        <ul>
          <li>Belong to a third party without their express permission</li>
          <li>Contain private or identifiable personal information of others without consent</li>
          <li>Are illegal, harmful, offensive, sexually explicit, or otherwise objectionable</li>
          <li>Infringe any copyright, trademark, or intellectual property right</li>
        </ul>
        <p>
          We reserve the right to remove any uploaded content that we determine violates these
          Terms, at our sole discretion and without notice.
        </p>
      </>
    ),
  },
  {
    id: 'ai-generated-results',
    title: '5. AI Generated Results',
    content: (
      <>
        <p>
          The AI-generated images produced by MyRoomsAI are intended for inspiration,
          visualization, and design planning purposes only. They are not architectural drawings,
          professional interior design specifications, or construction documents.
        </p>
        <p>
          AI-generated results are produced automatically and may not perfectly match your
          expectations, instructions, or the physical characteristics of your room. We make no
          guarantee that any generated image will meet your specific requirements or be free of
          errors or visual artefacts.
        </p>
        <p>
          Generated images should be used as a creative reference only. You should consult a
          qualified interior designer or architect before making any purchasing or construction
          decisions based on generated output.
        </p>
      </>
    ),
  },
  {
    id: 'credits-and-payments',
    title: '6. Credits and Payments',
    content: (
      <>
        <p>
          MyRoomsAI uses a credit-based system. Credits are digital usage units that are consumed
          each time you submit a room design generation request. Credits are deducted when a
          generation request is processed, regardless of whether you are satisfied with the result.
        </p>
        <p>
          Credits are purchased in packs and have no cash value. They are non-transferable and may
          not be sold or exchanged.
        </p>
        <p>
          Payments are processed by a third-party payment provider. MyRoomsAI does not directly
          store or handle your payment card details. By making a purchase, you also agree to the
          terms and privacy policy of the applicable payment provider.
        </p>
        <p>
          All prices are displayed on the pricing page and are subject to change. Any applicable
          taxes may be added at checkout depending on your location.
        </p>
      </>
    ),
  },
  {
    id: 'refund-policy',
    title: '7. Refund Policy',
    content: (
      <>
        <p>
          Because AI generation processing costs are incurred immediately when a request is
          submitted, credits that have been used to generate images are generally non-refundable.
        </p>
        <p>
          Refunds on unused credits may be considered on a case-by-case basis where required by
          applicable law, or in exceptional circumstances at our sole discretion. To request a
          refund, contact us at{' '}
          <a href="mailto:support@myroomsai.com">support@myroomsai.com</a> with your account
          details and the reason for your request.
        </p>
        <p>
          We reserve the right to decline refund requests where credits have been partially or
          fully consumed, or where we believe the request is abusive or fraudulent.
        </p>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    title: '8. Acceptable Use',
    content: (
      <>
        <p>You agree not to use MyRoomsAI to:</p>
        <ul>
          <li>Violate any applicable law or regulation</li>
          <li>Upload or generate content that is illegal, harmful, hateful, or obscene</li>
          <li>Attempt to reverse-engineer, scrape, or copy any part of the service</li>
          <li>Circumvent any access controls, rate limits, or credit systems</li>
          <li>Use automated scripts or bots to access the service without our written permission</li>
          <li>Distribute, resell, or sublicence access to the service to third parties</li>
          <li>
            Engage in any activity that could damage, disable, or impair the service or its servers
          </li>
        </ul>
        <p>
          Violation of these rules may result in immediate account suspension or termination
          without refund.
        </p>
      </>
    ),
  },
  {
    id: 'intellectual-property',
    title: '9. Intellectual Property',
    content: (
      <>
        <p>
          The MyRoomsAI platform, brand, logo, website design, software, and all related content
          are the property of MyRoomsAI and are protected by applicable intellectual property laws.
          You may not copy, reproduce, or distribute any part of the platform without our prior
          written consent.
        </p>
        <p>
          AI-generated images created using your uploaded content and your selected preferences are
          provided to you for your personal or professional use. You are responsible for ensuring
          your use of generated images complies with all applicable laws and does not infringe on
          third-party rights.
        </p>
        <p>
          We do not claim ownership of the original images you upload. However, by using the
          service you represent and warrant that you have the necessary rights to the images you
          provide.
        </p>
      </>
    ),
  },
  {
    id: 'service-availability',
    title: '10. Service Availability',
    content: (
      <>
        <p>
          We aim to keep MyRoomsAI available at all times, but we do not guarantee uninterrupted
          or error-free access to the service. The service may be temporarily unavailable due to
          maintenance, updates, or technical issues beyond our control.
        </p>
        <p>
          We reserve the right to modify, suspend, or discontinue any part of the service at any
          time with or without notice. We will not be liable to you or any third party for any such
          modification, suspension, or discontinuation.
        </p>
      </>
    ),
  },
  {
    id: 'limitation-of-liability',
    title: '11. Limitation of Liability',
    content: (
      <>
        <p>
          To the fullest extent permitted by applicable law, MyRoomsAI and its operators, employees,
          and affiliates shall not be liable for any indirect, incidental, special, consequential,
          or punitive damages arising from your use of, or inability to use, the service.
        </p>
        <p>
          Our total liability to you for any claim arising out of or relating to these Terms or
          the service shall not exceed the amount you paid to MyRoomsAI in the twelve months
          preceding the claim.
        </p>
        <p>
          Some jurisdictions do not allow the exclusion of certain warranties or the limitation of
          liability for certain types of damages. In such jurisdictions, our liability is limited
          to the maximum extent permitted by law.
        </p>
      </>
    ),
  },
  {
    id: 'account-suspension',
    title: '12. Account Suspension or Termination',
    content: (
      <>
        <p>
          We reserve the right to suspend or permanently terminate your account at any time if we
          determine, in our sole discretion, that you have violated these Terms, engaged in
          fraudulent activity, or posed a risk to the service or other users.
        </p>
        <p>
          Upon termination, your access to the service and any remaining credits will be forfeited.
          We are not obligated to provide a refund for unused credits in cases of termination due
          to a violation of these Terms.
        </p>
        <p>
          You may close your account at any time by contacting us at{' '}
          <a href="mailto:support@myroomsai.com">support@myroomsai.com</a>.
        </p>
      </>
    ),
  },
  {
    id: 'changes-to-terms',
    title: '13. Changes to These Terms',
    content: (
      <>
        <p>
          We may update these Terms and Conditions from time to time. When we do, we will revise
          the effective date at the top of this page. Where changes are material, we may also
          notify you via email or a notice on the website.
        </p>
        <p>
          Your continued use of the service after any changes to these Terms constitutes your
          acceptance of the updated Terms. If you do not agree to the revised Terms, please stop
          using the service.
        </p>
      </>
    ),
  },
  {
    id: 'contact',
    title: '14. Contact Information',
    content: (
      <>
        <p>
          If you have any questions, concerns, or requests regarding these Terms and Conditions,
          please contact us at:
        </p>
        <p>
          <strong>MyRoomsAI</strong>
          <br />
          Website:{' '}
          <a href="https://myroomsai.com">https://myroomsai.com</a>
          <br />
          Email:{' '}
          <a href="mailto:support@myroomsai.com">support@myroomsai.com</a>
        </p>
      </>
    ),
  },
];

function TermsPage() {
  return (
    <div className="page landing-bg">
      <Seo
        title="Terms & Conditions | MyRoomsAI"
        description="Terms and Conditions for using MyRoomsAI, an AI-powered room design and interior visualization service."
        canonicalPath="/terms-and-conditions"
        keywords="MyRoomsAI terms and conditions, AI room design terms, usage policy, credits policy"
      />
      <Navbar />

      <section className="container section fp-hero">
        <span className="credits-badge">Legal</span>
        <h1 className="fp-hero-heading">Terms &amp; Conditions</h1>
        <p className="credits-subtitle">Effective date: {EFFECTIVE_DATE}</p>
      </section>

      <section className="container section" style={{ paddingTop: 0 }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>

          <div className="card" style={{ marginBottom: '2rem', padding: '1.25rem 1.5rem' }}>
            <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.6' }}>
              <strong>Disclaimer:</strong> These Terms and Conditions are provided for general
              website use and payment registration purposes. They should be reviewed by a qualified
              legal professional before final publication.
            </p>
          </div>

          {sections.map(({ id, title, content }) => (
            <div key={id} style={{ marginBottom: '2.5rem' }}>
              <h2
                style={{
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  marginBottom: '0.75rem',
                  paddingBottom: '0.4rem',
                  borderBottom: '1px solid var(--border, rgba(255,255,255,0.08))',
                }}
              >
                {title}
              </h2>
              <div
                style={{
                  fontSize: '0.95rem',
                  lineHeight: '1.8',
                  opacity: 0.85,
                }}
              >
                {content}
              </div>
            </div>
          ))}

        </div>
      </section>

      <Footer />
    </div>
  );
}

export default TermsPage;
